import React, { useMemo, useState } from "react";

import { Button, Spinner, StatePanel } from "../../components/ui";
import useTradeFeed from "../../tradeFeed/useTradeFeed";
import TradeFeedCard from "../../tradeFeed/TradeFeedCard";
import TradeFeedModal from "../../tradeFeed/TradeFeedModal";
import collapseForwards from "../../tradeFeed/collapseForwards";

/**
 * The Trade Feed page's own search for this reference, inside the Core List
 * panel (Alex, 2026-10-20). Every message that MENTIONS the reference — not
 * only the ones that became an approved price, which is what the other tabs
 * show. Same hook, same cards, same "one card per forwarded message" collapse
 * as the page, so the two can never disagree about what the feed holds.
 *
 * Master-admin only, decided by the caller: /api/trade-feed is, because the
 * message collection has no store field.
 */
const CoreListTradeFeed = ({ reference }) => {
  const query = String(reference || "").trim();
  const [openItem, setOpenItem] = useState(null);
  const { items, isLoading, isLoadingMore, hasMore, error, loadMore } = useTradeFeed({ query });
  const visible = useMemo(() => collapseForwards(items), [items]);

  if (!query) {
    return <StatePanel state="empty" title="No reference" description="This item has no reference to search the feed for." />;
  }

  if (isLoading && !visible.length) {
    return (
      <p className="cl-match__sub" role="status">
        <Spinner size="sm" inline label="Searching" /> Searching the Trade Feed for {query}…
      </p>
    );
  }

  if (error && !visible.length) {
    return (
      <StatePanel state="error" title="Could not search the Trade Feed" description="Try again in a moment." />
    );
  }

  if (!visible.length && !hasMore) {
    return (
      <StatePanel state="empty" title="No messages" description={`No Trade Feed message mentions ${query}.`} />
    );
  }

  return (
    <>
      <p className="cl-match__sub">Trade Feed messages mentioning {query}, newest first.</p>

      <div className="cl-match__grid">
        {visible.map((item) => (
          <TradeFeedCard key={item.id} item={item} onOpen={setOpenItem} query={query} />
        ))}
      </div>

      {hasMore && (
        <nav className="cl-match__pager">
          {isLoadingMore ? (
            <span role="status">
              <Spinner size="sm" inline label="Loading" /> Loading more…
            </span>
          ) : (
            <Button size="sm" variant="secondary" onClick={loadMore}>
              Load more
            </Button>
          )}
        </nav>
      )}

      <TradeFeedModal item={openItem} query={query} onClose={() => setOpenItem(null)} />
    </>
  );
};

export default CoreListTradeFeed;
